// CareLogix™ ORACLE — Global Capital Command™ (16th constitutional domain).
// Dark green / gold theme preserved.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PALETTE, scoreColor } from "@/components/animation/MotionShared";
import axios from "axios";
import { getAdminToken } from "@/api/adminOneClient";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND}/api/oracle/capital`;

export default function OracleGlobalCapitalCommand() {
  const [constitution, setConstitution] = useState(null);
  const [dashboard, setDashboard]       = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [applications, setApplications] = useState([]);
  const [pipelineCounts, setPipelineCounts] = useState({});
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const adminToken = getAdminToken();
    try {
      // Phase 40A — Public/member surfaces consume the sanitized
      // /api/oracle/capital/public payload. Only AdminOne operators (with a
      // valid AdminOne JWT) load the raw /constitution which exposes the
      // agent swarm, adminone_widgets, and provider integration topology.
      const constitutionRequest = adminToken
        ? axios.get(`${API}/constitution`).catch(() => ({ data: null }))
        : axios.get(`${API}/public`);
      const requests = [
        constitutionRequest,
        adminToken
          ? axios.get(`${API}/adminone/dashboard`, { headers: { Authorization: `Bearer ${adminToken}` } }).catch(() => ({ data: null }))
          : Promise.resolve({ data: null }),
        axios.get(`${API}/opportunities?limit=12`),
        axios.get(`${API}/applications`),
      ];
      const [c, d, o, a] = await Promise.all(requests);
      setConstitution(c.data);
      setDashboard(d.data);
      setOpportunities(o.data?.opportunities || []);
      setApplications(a.data?.applications || []);
      setPipelineCounts(a.data?.pipeline_counts || {});
    } catch (e) { setError(String(e.message || e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const seedDemo = async () => {
    const samples = [
      { title: "HRSA Rural Health Network Planning Grant",        source: "HRSA",         region: "United States", award_amount: 500000,  eligibility: 88, strategic_fit: 82 },
      { title: "Gates Foundation Digital Health Africa",          source: "Gates Foundation", region: "Nigeria",     award_amount: 2500000, eligibility: 76, strategic_fit: 91 },
      { title: "Optum Ventures Series A Healthcare AI",            source: "Optum Ventures",   region: "United States", award_amount: 8000000, eligibility: 65, strategic_fit: 88 },
      { title: "African Development Bank Health Infrastructure",   source: "AfDB",         region: "Kenya",         award_amount: 5000000, eligibility: 71, strategic_fit: 85 },
      { title: "Republic Equity Crowdfunding Round",               source: "Republic",     region: "United States", award_amount: 1500000, eligibility: 80, strategic_fit: 70 },
    ];
    for (const s of samples) { try { await axios.post(`${API}/opportunities`, s); } catch (_) {} }
    await load();
  };

  const swarm = constitution?.agent_swarm || {};
  const widgets = constitution?.adminone_widgets || [];
  const profile = constitution?.carelogix_funding_profile || {};
  const integrations = constitution?.service_integrations || {};
  const pipelineStages = constitution?.pipeline_stages || [];

  return (
    <main style={S.shell} data-testid="oracle-capital-command">
      <div style={S.glow} aria-hidden="true" />
      <header style={S.header}>
        <div style={S.headerLeft}>
          <Link to="/" style={S.back} data-testid="capital-back">← ORACLE</Link>
          <div>
            <div style={S.eyebrow}>ORACLE CAPITAL & EVENT INTELLIGENCE™</div>
            <h1 style={S.title} data-testid="capital-title">ORACLE Capital & Event Intelligence™</h1>
            <p style={S.sub}>Discover grants, investors, venture capital, foundations, and strategic funding opportunities — scored by ORACLE Capital Intelligence™.</p>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
          <button onClick={load} style={S.btnGhost} data-testid="capital-refresh">↻ Refresh</button>
          <button onClick={seedDemo} style={S.btnSolid} data-testid="capital-seed">+ Seed sample opportunities</button>
        </div>
      </header>

      {/* Phase 42F — Capital & Event Intelligence tab strip. Each tab is a
          presentation-only filter; no autonomous trade/wager actions. The
          "Trade Research" tab is explicitly NOT "Trade Now". */}
      <nav data-testid="capital-tabs" aria-label="Capital tabs" style={{
        display: "flex", gap: 8, flexWrap: "wrap", margin: "12px 0 8px",
        padding: "6px 4px", borderTop: "1px solid rgba(245,200,66,0.15)",
        borderBottom: "1px solid rgba(245,200,66,0.15)",
      }}>
        {[
          { id: "stocks",     label: "Stocks",     testid: "capital-tab-stocks" },
          { id: "crypto",     label: "Crypto",     testid: "capital-tab-crypto" },
          { id: "forex",      label: "Forex / OANDA", testid: "capital-tab-forex" },
          { id: "options",    label: "Options",    testid: "capital-tab-options" },
          { id: "sports",     label: "Sports",     testid: "capital-tab-sports" },
          { id: "retirement", label: "Retirement", testid: "capital-tab-retirement" },
          { id: "watchlists", label: "Watchlists", testid: "capital-tab-watchlists" },
          { id: "reports",    label: "Reports",    testid: "capital-tab-reports" },
          { id: "trade",      label: "Trade Research", testid: "capital-tab-trade-research" },
        ].map((t) => (
          <span key={t.id} data-testid={t.testid} style={{
            padding: "6px 12px", borderRadius: 999, fontSize: 12,
            background: "rgba(245,200,66,0.08)", color: "#f5f5dc",
            border: "1px solid rgba(245,200,66,0.20)", cursor: "default",
            fontFamily: "'Space Mono', monospace",
          }}>{t.label}</span>
        ))}
      </nav>
      <p data-testid="capital-disclaimer" style={{
        color: "#d4d4aa", fontSize: 12, lineHeight: 1.5, margin: "8px 0 14px",
        background: "rgba(245,200,66,0.04)", border: "1px solid rgba(245,200,66,0.15)",
        borderRadius: 8, padding: "10px 12px",
      }}>
        Research only. Not financial advice. Human decision required. Sports content is entertainment and research
        only — not betting advice; 21+ where applicable; jurisdiction rules apply.
      </p>

      {error && <div style={S.err} data-testid="capital-error">{error}</div>}
      {loading && !dashboard && <div style={S.sub}>Loading Capital Command…</div>}

      {/* Top KPI strip — 4 hero widgets */}
      <section style={S.kpis} data-testid="capital-kpis">
        <KPI label="Open Opportunities"     value={dashboard?.open_opportunities ?? 0} />
        <KPI label="Applications"           value={dashboard?.applications_total ?? 0} />
        <KPI label="Pipeline Value"         value={`$${(dashboard?.pipeline_value_usd ?? 0).toLocaleString()}`} />
        <KPI label="Awards Received"        value={dashboard?.awards_received ?? 0} />
      </section>

      {/* Capital widgets row — only visible to AdminOne operators */}
      {getAdminToken() && (
        <section style={S.panel} data-testid="capital-widgets">
          <header style={S.panelHead}><h2 style={S.panelTitle}>Capital Widgets</h2><span style={S.meta}>{widgets.length} surfaces</span></header>
          <div style={S.widgetGrid}>
            {widgets.map((w) => <span key={w} style={S.widgetChip} data-testid={`capital-widget-${w}`}>{w}</span>)}
          </div>
        </section>
      )}

      {/* Capital Research Engine panel — customer-friendly relabel of the
          underlying multi-agent swarm. Same data; clearer copy. */}
      <section style={S.panel} data-testid="capital-swarm">
        <header style={S.panelHead}><h2 style={S.panelTitle}>Capital Research Engine™</h2><span style={S.meta}>live coverage</span></header>
        <div style={S.swarmGrid}>
          {Object.entries(swarm).map(([name, a]) => (
            <motion.article key={name} style={S.swarmCard} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} data-testid={`capital-agent-${name}`}>
              <div style={S.swarmHead}>
                <span style={S.swarmName}>{name}</span>
                <span style={{ ...S.statusPill, color: statusColor(a.status), borderColor: statusColor(a.status) }}>{a.status}</span>
              </div>
              <div style={S.swarmRole}>{a.role}</div>
              {(a.sources || a.countries) && (
                <div style={S.swarmSources}>
                  {(a.sources || a.countries || []).slice(0, 6).map((s) => <span key={s} style={S.sourceTag}>{s}</span>)}
                </div>
              )}
            </motion.article>
          ))}
        </div>
      </section>

      {/* Opportunities */}
      <section style={S.panel} data-testid="capital-opportunities">
        <header style={S.panelHead}><h2 style={S.panelTitle}>Opportunities · scored by Opportunity Score Engine™</h2><span style={S.meta}>{opportunities.length} live</span></header>
        {!opportunities.length && <div style={S.sub}>No opportunities yet. Click <strong>+ Seed sample opportunities</strong> above.</div>}
        <div style={S.oppGrid}>
          {opportunities.map((o) => (
            <article key={o.opportunity_id} style={S.oppCard} data-testid={`capital-opp-${o.opportunity_id}`}>
              <div style={S.oppHead}>
                <h4 style={S.oppTitle}>{o.title}</h4>
                <span style={{ ...S.scorePill, color: scoreColor(o.scores?.opportunity_score || 0), borderColor: scoreColor(o.scores?.opportunity_score || 0) }}>{o.scores?.opportunity_score || 0}</span>
              </div>
              <div style={S.oppMeta}>{o.source || "—"} · {o.region || "—"} · ${Number(o.award_amount || 0).toLocaleString()}</div>
              <div style={S.oppMeta}>{o.scores?.rating} · prob {o.scores?.funding_probability}% · risk {o.scores?.risk_level} · {o.scores?.time_to_apply}d</div>
            </article>
          ))}
        </div>
      </section>

      {/* Capital CRM pipeline */}
      <section style={S.panel} data-testid="capital-pipeline">
        <header style={S.panelHead}><h2 style={S.panelTitle}>Capital CRM™ Pipeline</h2><span style={S.meta}>{applications.length} applications</span></header>
        <div style={S.pipeline}>
          {pipelineStages.map((stage) => (
            <div key={stage} style={S.pipelineCol} data-testid={`capital-pipeline-${stage}`}>
              <div style={S.pipelineStage}>{stage}</div>
              <div style={S.pipelineCount}>{pipelineCounts[stage] || 0}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Africa Capital Command */}
      <section style={S.panel} data-testid="capital-africa">
        <header style={S.panelHead}><h2 style={S.panelTitle}>Africa Capital Command™</h2><span style={S.meta}>{Object.values(constitution?.africa_regions || {}).flat().length} countries · {(constitution?.africa_sources || []).length} sources</span></header>
        <div style={S.africaGrid}>
          {Object.entries(constitution?.africa_regions || {}).map(([region, countries]) => (
            <div key={region} style={S.africaCard}>
              <div style={S.africaRegion}>{region}</div>
              <div style={S.africaCountries}>{countries.map((c) => <span key={c} style={S.countryChip}>{c}</span>)}</div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 8 }}>
          <div style={S.meta}>Monitored sources:</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
            {(constitution?.africa_sources || []).map((s) => <span key={s} style={S.sourceTag}>{s}</span>)}
          </div>
        </div>
      </section>

      {/* Service integrations — truthful */}
      <section style={S.panel} data-testid="capital-integrations">
        <header style={S.panelHead}><h2 style={S.panelTitle}>Service Integrations</h2><span style={S.meta}>truthful status</span></header>
        <div style={S.intGrid}>
          {Object.entries(integrations).map(([svc, status]) => (
            <span key={svc} style={{ ...S.intPill, color: statusColor(status), borderColor: statusColor(status) }} data-testid={`capital-int-${svc}`}>
              {svc} · {status}
            </span>
          ))}
        </div>
      </section>

      {/* CareLogix funding profile */}
      <section style={S.panel} data-testid="capital-profile">
        <header style={S.panelHead}><h2 style={S.panelTitle}>CareLogix Funding Profile</h2></header>
        <div style={S.profGrid}>
          <div><strong style={S.profLbl}>Organization:</strong> {profile.organization}</div>
          <div><strong style={S.profLbl}>Sector:</strong> {profile.sector}</div>
          <div><strong style={S.profLbl}>Entity:</strong> {profile.entity_type} · {profile.stage}</div>
          <div><strong style={S.profLbl}>Jurisdictions:</strong> {(profile.jurisdictions || []).join(", ")}</div>
          <div><strong style={S.profLbl}>Subsectors:</strong> {(profile.subsectors || []).join(" · ")}</div>
          <div><strong style={S.profLbl}>Capital Tracks:</strong> {(profile.capital_tracks || []).join(" · ")}</div>
        </div>
      </section>

      <section style={S.caveats} data-testid="capital-caveats">
        <h3 style={S.caveatsTitle}>TRUTHFUL CAVEATS</h3>
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {(constitution?.truthful_caveats || []).map((c, i) => <li key={i}>{c}</li>)}
        </ul>
      </section>
    </main>
  );
}

function statusColor(s) {
  if (s === "ACTIVE") return "#34d399";
  if (s === "PARTIAL") return "#2dd4bf";
  if (s === "FRAMEWORK_READY") return "#fde68a";
  if (s === "MOCKED" || s === "AWAITING_KEYS") return "#fbbf24";
  return "#94a3b8";
}

function KPI({ label, value }) {
  return (
    <div style={S.kpi}>
      <div style={S.kpiLbl}>{label}</div>
      <div style={S.kpiVal}>{value}</div>
    </div>
  );
}

const S = {
  shell:    { position: "relative", minHeight: "100vh", padding: "20px clamp(12px, 3vw, 28px) 60px", background: "radial-gradient(circle at 50% 0%, rgba(15,45,30,0.45) 0%, rgba(4,6,8,1) 70%)", color: PALETTE.slate3, display: "flex", flexDirection: "column", gap: 14, fontFamily: "'Figtree', 'Inter', system-ui, sans-serif" },
  glow:     { position: "absolute", inset: 0, backgroundImage: "radial-gradient(ellipse 500px 280px at 50% 0%, rgba(197,160,89,0.12), transparent 65%)", pointerEvents: "none" },
  header:   { position: "relative", display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 },
  headerLeft:{ display: "flex", gap: 16, alignItems: "flex-start" },
  back:     { color: PALETTE.gold, textDecoration: "none", fontFamily: "'Space Mono', monospace", fontSize: 12, padding: "6px 12px", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 999 },
  eyebrow:  { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.24em" },
  title:    { margin: "4px 0 0", color: PALETTE.goldBright, fontSize: "clamp(20px, 3vw, 28px)", letterSpacing: "0.08em", fontWeight: 800 },
  sub:      { margin: "4px 0 0", color: PALETTE.slate4, fontSize: 13 },
  btnGhost: { appearance: "none", background: "transparent", border: `1px dashed ${PALETTE.goldGlow}`, color: PALETTE.gold, padding: "6px 11px", borderRadius: 999, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11 },
  btnSolid: { appearance: "none", background: PALETTE.gold, color: PALETTE.bgDeep, border: "none", padding: "6px 12px", borderRadius: 999, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 800 },
  err:      { color: PALETTE.red, background: "rgba(239,68,68,0.06)", border: `1px solid ${PALETTE.red}`, padding: "6px 10px", borderRadius: 8, fontSize: 12.5 },
  kpis:     { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 },
  kpi:      { background: "linear-gradient(180deg, rgba(8,32,22,0.55), rgba(4,6,8,0.85))", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 12, padding: 12 },
  kpiLbl:   { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.16em" },
  kpiVal:   { color: PALETTE.goldBright, fontFamily: "'Space Mono', monospace", fontSize: 22, fontWeight: 800, marginTop: 4 },
  panel:    { background: "linear-gradient(180deg, rgba(8,32,22,0.55), rgba(4,6,8,0.85))", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 14, padding: 14, display: "flex", flexDirection: "column", gap: 10 },
  panelHead:{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, flexWrap: "wrap" },
  panelTitle:{ margin: 0, color: PALETTE.goldBright, fontSize: 15, letterSpacing: "0.04em" },
  meta:     { color: PALETTE.slate4, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.10em" },
  widgetGrid:{ display: "flex", flexWrap: "wrap", gap: 6 },
  widgetChip:{ color: PALETTE.gold, border: `1px solid ${PALETTE.goldGlow}`, padding: "4px 10px", borderRadius: 999, fontFamily: "'Space Mono', monospace", fontSize: 11 },
  swarmGrid:{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 8 },
  swarmCard:{ background: "#040608", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, padding: 10, display: "flex", flexDirection: "column", gap: 4 },
  swarmHead:{ display: "flex", justifyContent: "space-between", alignItems: "center" },
  swarmName:{ color: PALETTE.goldBright, fontFamily: "'Space Mono', monospace", fontSize: 12.5, fontWeight: 800 },
  swarmRole:{ color: PALETTE.slate4, fontSize: 11.5 },
  swarmSources:{ display: "flex", flexWrap: "wrap", gap: 3, marginTop: 4 },
  sourceTag:{ color: PALETTE.slate3, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 6, padding: "2px 6px", fontSize: 10, fontFamily: "'Space Mono', monospace" },
  statusPill:{ fontFamily: "'Space Mono', monospace", fontSize: 9.5, padding: "2px 7px", border: "1px solid", borderRadius: 999, letterSpacing: "0.08em" },
  oppGrid:  { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 8 },
  oppCard:  { background: "#040608", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, padding: 10, display: "flex", flexDirection: "column", gap: 4 },
  oppHead:  { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 },
  oppTitle: { margin: 0, color: PALETTE.goldBright, fontSize: 13.5 },
  oppMeta:  { color: PALETTE.slate4, fontFamily: "'Space Mono', monospace", fontSize: 10.5 },
  scorePill:{ fontFamily: "'Space Mono', monospace", fontSize: 13, fontWeight: 800, padding: "3px 10px", border: "1px solid", borderRadius: 999 },
  pipeline: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))", gap: 6 },
  pipelineCol:{ background: "#040608", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8, padding: 8, textAlign: "center" },
  pipelineStage:{ color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.10em" },
  pipelineCount:{ color: PALETTE.goldBright, fontFamily: "'Space Mono', monospace", fontSize: 20, fontWeight: 800, marginTop: 4 },
  africaGrid:{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 8 },
  africaCard:{ background: "#040608", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, padding: 10 },
  africaRegion:{ color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.10em" },
  africaCountries:{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 },
  countryChip:{ color: PALETTE.goldBright, border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 6, padding: "2px 8px", fontFamily: "'Space Mono', monospace", fontSize: 10 },
  intGrid:  { display: "flex", flexWrap: "wrap", gap: 6 },
  intPill:  { fontFamily: "'Space Mono', monospace", fontSize: 11, padding: "3px 10px", border: "1px solid", borderRadius: 999, letterSpacing: "0.06em" },
  profGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 6, color: PALETTE.slate3, fontSize: 12.5, lineHeight: 1.5 },
  profLbl:  { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.08em" },
  caveats:  { background: "rgba(45,212,191,0.06)", border: "1px solid rgba(45,212,191,0.30)", borderRadius: 12, padding: 12, color: PALETTE.slate3, fontSize: 12.5 },
  caveatsTitle:{ color: "#2dd4bf", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.16em", margin: "0 0 6px" },
};
